import { useDispatch, useSelector } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';
import { getScopeStart } from 'lib/selectors/getScopeStart';
import { getShift } from 'lib/selectors/getShift';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import {
  actionSetLastNonIdle,
  actionSetMachineOperationalStatus,
} from 'lib/actions';
import { getWaitingForWorkEnabled } from 'lib/selectors/getWaitingForWorkEnabled';
import { useCallback, useEffect } from 'react';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getMachineOperationalStatus } from 'lib/selectors/getMachineOperationalStatus';
import { getLatestNonIdleInterval } from 'lib/selectors/getLatestNonIdleInterval';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { useDebouncedLazyQuery } from './useDebouncedLazyQuery';
import { GET_MACHINE_OPERATIONAL_STATUS } from '../api/queries';

export const useMachineOperationalStatus = () => {
  const dispatch = useDispatch();

  const machine = useSelector(getMachine);
  const scopeStart = useSelector(getScopeStart);
  const shift = useSelector(getShift);
  const latestActivitySet = useSelector(getLatestActivitySet);
  const waitingForWorkEnabled = useSelector(getWaitingForWorkEnabled);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const machineOperationalStatus = useSelector(getMachineOperationalStatus);
  const latestActivity = useSelector(getLatestActivity);
  const latestNonIdleInterval = useSelector(getLatestNonIdleInterval);

  const [requestMachineOperationalStatus] = useDebouncedLazyQuery(
    GET_MACHINE_OPERATIONAL_STATUS,
    {
      variables: {
        machineRef: machine.machineRef,
        input: {
          startDate: scopeStart,
          shift,
          ...(latestActivitySet && {
            activitySet: {
              ...latestActivitySet,
              activities: latestActivity ? [latestActivity] : [],
            },
          }),
          lastNonIdleInterval: latestNonIdleInterval,
        },
      },
      fetchPolicy: 'no-cache',
      onCompleted: (data) => {
        if (!data || typeof data !== 'object') {
          return;
        }

        const payload = data.machineOperationalStatus;
        if (!payload || typeof payload !== 'object') {
          return;
        }

        dispatch(actionSetLastNonIdle(payload.lastNonIdleInterval));
        dispatch(
          actionSetMachineOperationalStatus({
            status: payload.status,
            machineRef: payload.machineRef,
            since: payload.since,
          })
        );
      },
    },
    3000
  );

  useEffect(() => {
    if (
      machineOperationalStatus &&
      (!waitingForWorkEnabled || hasOpenActivitySet)
    ) {
      dispatch(actionSetMachineOperationalStatus(null));
    }
  }, [
    waitingForWorkEnabled,
    hasOpenActivitySet,
    machineOperationalStatus,
    dispatch,
  ]);

  const conditionalRequestMachineOperationalStatus = useCallback(() => {
    if (!waitingForWorkEnabled || hasOpenActivitySet) {
      return Promise.resolve();
    }
    return requestMachineOperationalStatus();
  }, [
    waitingForWorkEnabled,
    requestMachineOperationalStatus,
    hasOpenActivitySet,
  ]);

  return conditionalRequestMachineOperationalStatus;
};
